// 聊天轮询管理 Hook

import { useState, useEffect, useRef } from 'react';

// 轮询配置
export interface PollingConfig {
  interval: number;
  enabled: boolean;
  delay: number;
  onError?: (error: Error) => void; // 错误回调
}

// 轮询管理 Hook
export function useChatPolling(
  isOpen: boolean,
  isInitialized: boolean,
  email: string,
  tempUserKey: string,
  fetchMessages: (email: string, tempUserKey: string, page?: number, pagesize?: number, isPolling?: boolean) => Promise<void>,
  config: PollingConfig = {
    interval: 50000, // 50秒
    enabled: true,
    delay: 5000 // 5秒延迟
  }
) {
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // 启动轮询
  const startPolling = () => {
    if (!config.enabled || pollingInterval) return;

    const interval = setInterval(() => {
      fetchMessages(email, tempUserKey, 1, 10, true);
    }, config.interval);

    setPollingInterval(interval);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // 管理轮询生命周期
  useEffect(() => {
    let delayTimer: NodeJS.Timeout | null = null;
    
    // 只有在聊天窗口打开且初始化完成时才获取消息
    if (isOpen && isInitialized && email && tempUserKey) {
      // 立即获取一次消息
      fetchMessages(email, tempUserKey, 1, 10, false).catch(error => {
        config.onError?.(error);
      });

      // 延迟启动轮询
      delayTimer = setTimeout(() => {
        startPolling();
        isInitializedRef.current = true;
      }, config.delay);

      return () => {
        if (delayTimer) {
          clearTimeout(delayTimer);
          delayTimer = null;
        }
        stopPolling();
      };
    } else {
      // 聊天框关闭时清除轮询
      stopPolling();
      isInitializedRef.current = false;
    }
  }, [isOpen, isInitialized, email, tempUserKey, config.interval, config.delay, config.enabled]);

  // 页面可见性：隐藏时暂停轮询，重新可见时立即拉一次
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (isOpen && isInitialized && email && tempUserKey) {
        // 重新可见：先拉一次，再恢复轮询
        fetchMessages(email, tempUserKey, 1, 10, true).finally(() => startPolling());
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isOpen, isInitialized, email, tempUserKey, startPolling, stopPolling, fetchMessages]);

  // 清理轮询
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return {
    pollingInterval,
    startPolling,
    stopPolling,
    isPollingActive: !!pollingInterval
  };
}